// deps
import React, { useContext, useState, useCallback } from "react";

// internal-deps
import {
    EmiSelectionContext,
    EmiSelectionContextValue,
    IEMIResponse,
    IEMISelectionDetails,
} from "@payment-client/contexts/EmiSelectionContext";

// apis
import { getEmiDetails } from "@payment-client/apis/EMIDetails";

// constants
import { DOWNSTREAM_HOSTS } from "@payment-client/constants/Hosts";
import { TENANT } from "./constant";

// hooks
import { recordNREvent, NEWRELIC_TABLES } from "@swiggy-private/checkout-core";

interface EmiSelectionProviderProps {
    children: React.ReactNode;
}

/**
 * Provider component for EMI selection functionality.
 * Manages EMI details fetching, selection state, and provides context to child components.
 *
 * @param children - React components that will have access to EMI selection context
 * @returns EmiSelectionProvider component
 */
export function EmiSelectionProvider({ children }: EmiSelectionProviderProps): React.ReactElement {
    const [loadingEmi, setLoadingEmi] = useState(false);
    const [emiResponse, setEmiResponse] = useState<IEMIResponse | null>(null);

    const [selectedEmiDetails, setSelectedEmiDetails] = useState<IEMISelectionDetails | null>(null);
    const [emiError, setEmiError] = useState<Error | null>(null);

    const fetchEmiDetails = useCallback((cardReference?: string) => {
        setLoadingEmi(true);
        setEmiError(null);
        getEmiDetails(DOWNSTREAM_HOSTS.CHECKOUT_READ_HOST, {
            tenant: TENANT,
            cardReference,
        })
            .then((response) => {
                setEmiResponse(response);
            })
            .catch((error) => {
                setEmiError(error);

                recordNREvent({
                    eventType: NEWRELIC_TABLES.EMI_DASH_ERROR,
                    source: "emi_plan_list",
                    statusCode: error?.statusCode || "--",
                    errorMessage: error?.message || "--",
                    errorName: error?.name || "--",
                });
            })
            .then(() => {
                setLoadingEmi(false);
            });
    }, []);

    const updateEmiSelection = useCallback((selectionData: IEMISelectionDetails) => {
        setSelectedEmiDetails(selectionData);
    }, []);

    const contextValue: EmiSelectionContextValue = {
        loadingEmi,
        emiResponse,
        selectedEmiDetails,
        emiError,
        fetchEmiDetails,
        updateEmiSelection,
        setEmiResponse,
        setLoadingEmi,
    };

    return <EmiSelectionContext.Provider value={contextValue}>{children}</EmiSelectionContext.Provider>;
}

/**
 * Hook to use EMI selection context.
 * Must be used within an EmiSelectionProvider.
 *
 * @returns The EMI selection context value
 * @throws Error if used outside of EmiSelectionProvider
 */
export const useEmiSelection = () => {
    const context = useContext(EmiSelectionContext);
    if (!context) {
        throw new Error("useEmiSelection must be used within EmiSelectionProvider");
    }
    return context;
};
